import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Package,
  CreditCard,
  CheckCircle,
  Clock,
  Truck,
  MapPin,
  ChefHat,
  PackageCheck,
  Search,
  ArrowLeft,
  Copy,
  Check,
  MessageCircle,
  Crown,
  Store,
  ChevronRight,
  Heart,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const estadoConfig = {
  recibido: { icon: Package, label: "Pedido Recibido", color: "bg-blue-500", badge: "bg-[#DBEAFE] text-[#1E40AF]" },
  pago_pendiente: { icon: CreditCard, label: "Pago Pendiente", color: "bg-yellow-500", badge: "bg-[#FEF3C7] text-[#92400E]" },
  pago_confirmado: { icon: CheckCircle, label: "Pago Confirmado", color: "bg-green-500", badge: "bg-[#D1FAE5] text-[#065F46]" },
  confirmado: { icon: CheckCircle, label: "Confirmado", color: "bg-green-500", badge: "bg-[#D1FAE5] text-[#065F46]" },
  en_produccion: { icon: ChefHat, label: "En Producción", color: "bg-orange-500", badge: "bg-[#FEF3C7] text-[#92400E]" },
  listo: { icon: PackageCheck, label: "Listo", color: "bg-purple-500", badge: "bg-purple-100 text-purple-800" },
  en_ruta: { icon: Truck, label: "En Ruta", color: "bg-blue-600", badge: "bg-[#DBEAFE] text-[#1E40AF]" },
  entregado: { icon: MapPin, label: "Entregado", color: "bg-green-600", badge: "bg-[#D1FAE5] text-[#065F46]" },
  cancelado: { icon: Clock, label: "Cancelado", color: "bg-red-500", badge: "bg-red-100 text-red-700" },
};

const mensajesEstado = {
  recibido: "¡Recibimos tu antojo! Ya estamos alistando todo.",
  pago_pendiente: "Estamos esperando la confirmación de tu pago.",
  pago_confirmado: "¡Pago confirmado! La abuela ya tiene tu pedido en la lista.",
  confirmado: "Pedido confirmado y en la cola del horno.",
  en_produccion: "¡Tu pedido se está horneando con todo el amor de la abuela! 👵",
  listo: "¡Listo! Tu pedido salió del horno y espera su despacho.",
  en_ruta: "¡Tu pedido va en camino, calientito y fresco! 🚚",
  entregado: "¡Entregado! Disfruta cada bocado artesanal. 💛",
  cancelado: "Este pedido fue cancelado. Escríbenos si tienes dudas.",
};

export default function OrderTrackingPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();

  const [searchNumber, setSearchNumber] = useState(orderNumber || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sugeridos, setSugeridos] = useState([]);

  const fetchOrder = async (num) => {
    if (!num) return;
    setLoading(true);
    setError(null);
    try {
      const cleanNum = num.replace("#", "");
      const res = await fetch(`${API_URL}/api/catalogo/pedido/${cleanNum}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error("Pedido no encontrado");
        throw new Error("Error al buscar el pedido");
      }
      const data = await res.json();
      setOrder(data);
      if (orderNumber !== cleanNum) {
        navigate(`/pedido/${cleanNum}`, { replace: true });
      }
    } catch (err) {
      setError(err.message);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderNumber) fetchOrder(orderNumber);
  }, [orderNumber]);

  // Cross-sell: productos del catálogo que no están en el pedido
  useEffect(() => {
    if (!order) return;
    const cargar = async () => {
      try {
        const res = await fetch(`${API_URL}/api/catalogo/baruj-principal`);
        if (!res.ok) return;
        const data = await res.json();
        const enPedido = new Set((order.items || []).map((i) => i.producto_id));
        let candidatos = (data.productos || []).filter((p) => !enPedido.has(p.id));
        if (candidatos.length === 0) candidatos = data.productos || [];
        setSugeridos(candidatos.slice(0, 4));
      } catch {
        /* silencioso */
      }
    };
    cargar();
  }, [order]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrder(searchNumber);
  };

  const copyOrderNumber = () => {
    navigator.clipboard.writeText(order?.numero_pedido || "");
    setCopied(true);
    toast.success("Número copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const estadoActual = order ? estadoConfig[order.estado] : null;

  return (
    <div className="min-h-screen bg-[#FBF5E8]">
      {/* Header */}
      <div className="bg-[#4A2C0A] text-white py-4 px-4" data-testid="order-tracking-header">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-2 hover:bg-white/10 rounded-full" data-testid="tracking-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-['Playfair_Display'] text-lg">Seguimiento de Pedido</h1>
            <p className="font-['Poppins'] text-xs text-white/70">Baruj Shefa — hecho con amor de abuela</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-5 pb-10">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            type="text"
            placeholder="Número de pedido (ej: 5000)"
            value={searchNumber}
            onChange={(e) => setSearchNumber(e.target.value)}
            className="bg-white border-[#E5D4B3] font-['Poppins']"
            data-testid="tracking-search-input"
          />
          <Button type="submit" disabled={loading} className="bg-[#C8902A] hover:bg-[#B07D1E]" data-testid="tracking-search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </form>

        {loading && (
          <div className="text-center py-8">
            <div className="w-12 h-12 border-4 border-[#C8902A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="font-['Poppins'] text-[#4A2C0A]">Buscando pedido...</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-8" data-testid="tracking-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">😕</span>
            </div>
            <h2 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] mb-2">{error}</h2>
            <p className="font-['Poppins'] text-sm text-[#4A2C0A]/70">Verifica el número e intenta de nuevo.</p>
          </div>
        )}

        {order && !loading && (
          <div className="space-y-5">
            {/* Hero de estado */}
            <div className="rounded-2xl p-6 border-2 border-[#C8902A]/40 bg-gradient-to-b from-[#FFF9F0] to-white shadow-sm" data-testid="order-status-hero-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="font-['Playfair_Display'] text-2xl font-bold text-[#C8902A]">{order.numero_pedido}</span>
                  <button onClick={copyOrderNumber} className="p-1 hover:bg-[#FBF5E8] rounded-full" data-testid="copy-order-btn">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-[#4A2C0A]/50" />}
                  </button>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-['Poppins'] font-semibold ${estadoActual?.badge}`}>
                  {estadoActual?.label}
                </span>
              </div>

              <div className="flex items-center gap-4">
                {estadoActual && (
                  <div className={`w-14 h-14 shrink-0 rounded-full flex items-center justify-center text-white ${estadoActual.color} ${["en_produccion", "en_ruta"].includes(order.estado) ? "animate-pulse" : ""}`}>
                    <estadoActual.icon className="w-7 h-7" />
                  </div>
                )}
                <p className="font-['Playfair_Display'] text-lg leading-snug text-[#4A2C0A]">
                  {mensajesEstado[order.estado]}
                </p>
              </div>

              {/* Timeline */}
              <div className="mt-5 pt-4 border-t border-[#E5D4B3]/60 space-y-0">
                {order.timeline
                  .filter((t) => !["pago_pendiente", "pago_confirmado"].includes(t.estado) || order.estado === t.estado)
                  .map((step, index, arr) => {
                    const config = estadoConfig[step.estado];
                    const Icon = config?.icon || Clock;
                    return (
                      <div key={step.estado} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${step.completado ? (step.activo ? config?.color : "bg-green-500") : "bg-gray-200"}`}>
                            {step.completado ? <Icon className="w-3.5 h-3.5 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-400" />}
                          </div>
                          {index < arr.length - 1 && (
                            <div className={`w-0.5 h-6 ${step.completado ? "bg-green-300" : "bg-gray-200"}`} />
                          )}
                        </div>
                        <p className={`font-['Poppins'] text-sm pt-1 ${step.activo ? "text-[#C8902A] font-semibold" : step.completado ? "text-[#4A2C0A]" : "text-gray-400"}`}>
                          {config?.label}
                        </p>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Resumen + entrega */}
            <div className="bg-white rounded-2xl p-5 border border-[#E5D4B3] shadow-sm" data-testid="order-summary-card">
              <h3 className="font-['Playfair_Display'] text-[#4A2C0A] mb-3">Tu pedido</h3>
              <div className="space-y-2">
                {order.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="font-['Poppins'] text-sm text-[#4A2C0A]">{item.cantidad}x {item.producto_nombre}</span>
                    <span className="font-['Poppins'] text-sm text-[#C8902A] font-medium">${item.subtotal.toLocaleString("es-CO")}</span>
                  </div>
                ))}
                <div className="border-t border-[#E5D4B3] pt-2 mt-2 space-y-1.5">
                  <div className="flex justify-between font-['Poppins'] text-sm text-[#4A2C0A]/70">
                    <span>Subtotal</span><span>${order.subtotal.toLocaleString("es-CO")}</span>
                  </div>
                  {order.costo_domicilio > 0 && (
                    <div className="flex justify-between font-['Poppins'] text-sm text-[#4A2C0A]/70">
                      <span>Domicilio</span><span>${order.costo_domicilio.toLocaleString("es-CO")}</span>
                    </div>
                  )}
                  {order.descuento_club > 0 && (
                    <div className="flex justify-between font-['Poppins'] text-sm text-green-700">
                      <span>🎁 Beneficio Club</span><span>-${order.descuento_club.toLocaleString("es-CO")}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="font-['Playfair_Display'] text-lg text-[#4A2C0A]">Total</span>
                    <span className="font-['Playfair_Display'] text-xl font-bold text-[#C8902A]">${order.total.toLocaleString("es-CO")}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-[#E5D4B3]/60 space-y-2 font-['Poppins'] text-sm">
                <div className="flex items-center gap-2 text-[#4A2C0A]">
                  <Clock className="w-4 h-4 text-[#C8902A]" />
                  <span>
                    {new Date(order.fecha_entrega + "T12:00:00").toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" })}
                    {" · "}
                    {order.franja_horaria === "manana" && "Mañana (8am-12m)"}
                    {order.franja_horaria === "tarde" && "Tarde (12m-5pm)"}
                    {order.franja_horaria === "noche" && "Noche (5pm-8pm)"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[#4A2C0A]">
                  {order.tipo_entrega === "domicilio" ? <Truck className="w-4 h-4 text-[#C8902A]" /> : <MapPin className="w-4 h-4 text-[#C8902A]" />}
                  <span>{order.tipo_entrega === "domicilio" ? "Envío a domicilio" : "Recoger en punto"}</span>
                </div>
              </div>
            </div>

            {/* Venta cruzada */}
            {sugeridos.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-[#E5D4B3] shadow-sm" data-testid="cross-sell-section">
                <h3 className="font-['Playfair_Display'] text-lg text-[#4A2C0A]">Acompaña tu antojo</h3>
                <p className="font-['Poppins'] text-xs text-[#7A6552] mb-3">Delicias artesanales que se hornean perfecto junto a tu pedido</p>
                <div className="grid grid-cols-2 gap-3">
                  {sugeridos.map((p) => (
                    <Link
                      key={p.id}
                      to="/catalogo/baruj-principal"
                      className="group border border-[#E5D4B3] rounded-xl overflow-hidden bg-[#FFF9F0] hover:shadow-md transition-all duration-200"
                      data-testid={`cross-sell-product-${p.id}`}
                    >
                      <div className="h-20 bg-[#F5F0E8] flex items-center justify-center overflow-hidden">
                        {p.imagen_url ? (
                          <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <span className="text-3xl">🍰</span>
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="font-['Poppins'] text-xs font-semibold text-[#4A2C0A] line-clamp-1">{p.nombre}</p>
                        <p className="font-['Poppins'] text-sm text-[#C8902A] font-bold">${(p.precio || 0).toLocaleString("es-CO")}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/catalogo/baruj-principal" data-testid="cross-sell-cta">
                  <button className="mt-3 w-full bg-[#C8902A] hover:bg-[#B07D1E] text-white font-['Poppins'] font-semibold text-sm py-2.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5">
                    Pedir ahora <ChevronRight className="w-4 h-4 inline" />
                  </button>
                </Link>
              </div>
            )}

            {/* Club de la Abuela */}
            <div className="rounded-2xl p-5 bg-gradient-to-br from-[#4A2C0A] to-[#6B4318] text-amber-50 shadow-md" data-testid="club-abuela-invitation-card">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 shrink-0 rounded-full bg-[#C8902A]/30 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-[#F5D78E]" />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-lg leading-tight">¡Esta compra ya suma en el Club de la Abuela!</h3>
                  <p className="font-['Poppins'] text-xs text-amber-100/80 mt-1">
                    👋 De Visita → 🏠 De la Casa → 👑 Familia Baruj. Consulta tu nivel con tu celular y reclama tus regalos.
                  </p>
                </div>
              </div>
              <Link to="/club" data-testid="club-invitation-cta">
                <button className="mt-4 w-full bg-[#C8902A] hover:bg-[#B07D1E] text-white font-['Poppins'] font-semibold text-sm py-3 rounded-xl transition-all duration-200 hover:-translate-y-0.5">
                  Ver mis recompensas 👑
                </button>
              </Link>
            </div>

            {/* Conviértete en vendedor */}
            <div className="bg-white rounded-2xl p-5 border border-[#E5D4B3] shadow-sm" data-testid="vendedor-invitation-card">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 shrink-0 rounded-full bg-[#FBF5E8] border border-[#E5D4B3] flex items-center justify-center">
                  <Store className="w-5 h-5 text-[#C8902A]" />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-lg leading-tight text-[#4A2C0A]">¡Emprende y gana con Baruj Shefa!</h3>
                  <p className="font-['Poppins'] text-xs text-[#7A6552] mt-1">
                    Vende nuestras famosas tortas de cuajada en tu barrio o con tu red y gana comisiones por cada venta.
                  </p>
                </div>
              </div>
              <Link to="/registro-vendedor" data-testid="vendedor-invitation-cta">
                <button className="mt-4 w-full bg-[#FBF5E8] hover:bg-[#EFE6D3] text-[#4A2C0A] border border-[#C8902A] font-['Poppins'] font-semibold text-sm py-2.5 rounded-xl transition-all duration-200">
                  Quiero ser vendedor <ChevronRight className="w-4 h-4 inline" />
                </button>
              </Link>
            </div>

            {/* WhatsApp */}
            <div className="bg-[#25D366]/10 rounded-2xl p-4 border border-[#25D366]/30" data-testid="support-footer-section">
              <p className="font-['Poppins'] text-sm text-[#4A2C0A] mb-3">¿Dudas con tu entrega? Habla con nosotros</p>
              <Button
                onClick={() => window.open(`https://wa.me/573196536874?text=Hola, tengo una consulta sobre mi pedido ${order.numero_pedido}`, "_blank")}
                className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-xl"
                data-testid="whatsapp-support-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Contactar por WhatsApp
              </Button>
            </div>

            <p className="text-center font-['Poppins'] text-[11px] text-[#7A6552] flex items-center justify-center gap-1">
              Horneado artesanal con <Heart className="w-3 h-3 text-red-400 fill-red-400" /> por Baruj Shefa SAS
            </p>
          </div>
        )}

        {!order && !loading && !error && (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-[#E5D4B3] mx-auto mb-4" />
            <h2 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] mb-2">Busca tu pedido</h2>
            <p className="font-['Poppins'] text-sm text-[#4A2C0A]/70">Ingresa el número de pedido para ver su estado</p>
          </div>
        )}
      </div>
    </div>
  );
}
